import React from 'react';

interface SkeletonCardProps {
  variant?: 'default' | 'community' | 'compact';
}

export function SkeletonCard({ variant = 'default' }: SkeletonCardProps) {
  if (variant === 'community') {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
        <div className="h-32 sm:h-40 bg-gray-200" />
        <div className="p-5 pt-12 relative">
          <div className="absolute -top-8 left-4 w-12 h-12 rounded-full bg-gray-300 ring-4 ring-white" />
          <div className="h-5 bg-gray-200 rounded w-2/3 mb-3" />
          <div className="flex gap-2 mb-3">
            <div className="h-5 bg-gray-100 rounded-full w-16" />
          </div>
          <div className="h-4 bg-gray-100 rounded w-full mb-2" />
          <div className="h-4 bg-gray-100 rounded w-4/5 mb-4" />
          <div className="pt-3 border-t border-gray-100 flex justify-between">
            <div className="flex -space-x-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="w-6 h-6 rounded-full bg-gray-200 ring-2 ring-white" />
              ))}
            </div>
            <div className="h-4 bg-gray-100 rounded w-20" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-gray-200" />
            <div className="h-4 bg-gray-200 rounded w-24" />
          </div>
          <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-gray-100 rounded w-full mb-2" />
          <div className="h-4 bg-gray-100 rounded w-2/3" />
        </div>
      </div>
    );
  }

  // Default variant — matches EventCard / RecipeCard
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200" />
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-200" />
            <div className="h-4 bg-gray-200 rounded w-20" />
          </div>
          <div className="h-6 bg-gray-100 rounded-full w-16" />
        </div>
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded" />
            <div className="h-4 bg-gray-100 rounded w-32" />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded" />
            <div className="h-4 bg-gray-100 rounded w-40" />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded" />
            <div className="h-4 bg-gray-100 rounded w-28" />
          </div>
        </div>
      </div>
    </div>
  );
}

interface SkeletonGridProps {
  count?: number;
  variant?: 'default' | 'community' | 'compact';
  columns?: string;
}

export function SkeletonGrid({ count = 6, variant = 'default', columns = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' }: SkeletonGridProps) {
  return (
    <div className={`grid ${columns} gap-6`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} variant={variant} />
      ))}
    </div>
  );
}
